<template>
	<v-row>
		<v-col>
			<ui-box>
				<h5>Search for Customers or Orders</h5>
				<span class="grey--text text--darken-1 text-body-2">
					Search for order number, customer name, customer email, customer phone number, order voucher, individual voucher or pass link.
				</span>

				<v-divider class="mb-10 mt-4 opacity-80"></v-divider>

				<v-row>
					<v-col cols="10">
						<v-text-field
								v-model="searchQuery.searchText"
								class="rounded-lg"
								label="Search Text"
								outlined
								persistent-hint
								@keyup.enter="search"
								autocomplete="off"
						></v-text-field>
					</v-col>
					<v-col cols="2">
						<v-btn
								class="mb-6 ms-2 mt-2 rounded-lg"
								color="info"
								depressed
								@click="search"
						>
							<v-icon>
								mdi-magnify
							</v-icon>
							Search
						</v-btn>
					</v-col>
				</v-row>

				<v-row v-if="searchResult.error">
					<v-col>
						<span class="red--text text--darken-1 text-body-2">
							 {{ searchResult.message }}
						</span>
					</v-col>
				</v-row>

				<v-row v-if="searchResult.orders?.length > 0">
					<v-col cols="2">
						<h5>Orders</h5>
					</v-col>
					<v-col cols="10">
						<bw-table aria-label="data-table" :headers="headersOrders"
											:items="searchResult.orders" hide-search key="keyOrder">
							<template v-if="showPartner" v-slot:item.partnerId="{ item: order }">
								{{ order.partner_id }}
							</template>
							<template v-if="showPartner" v-slot:item.partnerName="{ item: order }">
								{{ order.partner_name }}
							</template>
							<template v-slot:item.id="{ item: order }">
								<div
										class="font-weight-bold text-subtitle-2 primary--text text--darken-4"
										style="flex-wrap: nowrap !important;"
								>
									<a :href="orderViewUrl(order.id)">{{ order.id }}</a>
								</div>
							</template>
							<template v-slot:item.voucher="{ item: order }">
								{{ order.voucher }}
							</template>
							<template v-slot:item.token="{ item: order }">
								{{ order.token }}
							</template>
							<template v-slot:item.customer="{ item: order }">
								{{ order.customer_name }}
							</template>
							<template v-slot:item.purchased="{ item: order }">
								{{ getLocalDateTime(order.purchased_at, order.partner_timezone) }}
							</template>
							<template v-slot:item.status="{ item: order }">
								{{ order.status }}
							</template>
						</bw-table>
					</v-col>
				</v-row>

				<v-row v-if="searchResult.customers?.length > 0">
					<v-col cols="2">
						<h5>Customers</h5>
					</v-col>
					<v-col cols="10">
						<bw-table aria-label="data-table" :headers="headersCustomers"
											:items="searchResult.customers" hide-search key="keyCustomer">
							<template v-if="showPartner" v-slot:item.partnerId="{ item: customer }">
								{{ customer.partner_id }}
							</template>
							<template v-if="showPartner" v-slot:item.partnerName="{ item: customer }">
								{{ customer.partner_name }}
							</template>
							<template v-slot:item.name="{ item: customer }">
								<div
										class="font-weight-bold text-subtitle-2 primary--text text--darken-4"
										style="flex-wrap: nowrap !important;"
								>
									<a :href="customerViewUrl(customer.id)">{{ customer.name }}</a>
								</div>
							</template>
							<template v-slot:item.email="{ item: customer }">
								{{ customer.email }}
							</template>
							<template v-slot:item.phone="{ item: customer }">
								{{ customer.phone }}
							</template>
							<template v-slot:item.voucher="{ item: customer }">
								{{ customer.voucher }}
							</template>
						</bw-table>
					</v-col>
				</v-row>

			</ui-box>
		</v-col>
	</v-row>
</template>

<script>

import { helpers } from '../../../helpers';

export default {
	name: "SearchCustomersOrders",
	props: {
		partner: {
			type: Object,
			default: () => ({})
		},
	},
	data() {
		return {
			searchQuery: { searchText: '' },
			searchResult: {},
		}
	},
	computed: {
		showPartner() {
			return this.partner === null || this.partner === undefined || this.partner.id === null || this.partner.id === undefined;
		},
		headersOrders() {
			const columns = [
				{
					align: 'start',
					mandatory: true,
					text: 'Order Id',
					value: 'id',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Voucher',
					value: 'voucher',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Pass Link',
					value: 'token',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Customer',
					value: 'customer',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Purchased',
					value: 'purchased',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Status',
					value: 'status',
					width: '10%'
				}
			];
			if(this.showPartner) {
				columns.unshift({
							align: 'start',
							mandatory: true,
							sortable: false,
							text: 'Partner Id',
							value: 'partnerId',
							width: '10%'
						},
						{
							align: 'start',
							mandatory: true,
							sortable: false,
							text: 'Partner Name',
							value: 'partnerName',
							width: '10%'
						},
				);
			}
			return columns;
		},
		headersCustomers() {
			const columns = [
				{
					align: 'start',
					mandatory: true,
					text: 'Name',
					value: 'name',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'email',
					value: 'email',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Phone',
					value: 'phone',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Voucher',
					value: 'voucher',
					width: '10%'
				}
			];
			if(this.showPartner) {
				columns.unshift({
							align: 'start',
							mandatory: true,
							sortable: false,
							text: 'Partner Id',
							value: 'partnerId',
							width: '10%'
						},
						{
							align: 'start',
							mandatory: true,
							sortable: false,
							text: 'Partner Name',
							value: 'partnerName',
							width: '10%'
						});
			}
			return columns;
		}
	},
	methods: {
		getLocalDateTime(dateTime, tz) {
			return helpers.getLocalDateTime(dateTime, tz);
		},
		orderViewUrl(orderId) {
			return helpers.getBaseUrl(this.partner, 'customers', 'main') + '/view-order/' + orderId;
		},
		customerViewUrl(customerId) {
			return helpers.getBaseUrl(this.partner, 'customers', 'main') + '/view-customer/' + customerId;
		},
		search() {
			this.searchResult = {};
			let url = helpers.getBaseUrl(this.partner, 'customers', 'main') + '/search/';
			axios.put(url, this.searchQuery)
					.then(response => {
						this.searchResult = response.data.searchResult;
					})
					.catch(error => {
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		}
	}
};
</script>
